import { apiInstance } from './api';
import {
  AdminBusinessInterface,
  PaginatedBusinessesResponse,
  BusinessMemberInterface,
  BusinessMemberPermissions,
  PlanInterface,
  BusinessMemberUserInterface,
} from '@app/models/business';
import { ListResponse } from '@app/models';

export const getBusinessesAdmin = async (
  page: number = 1,
  perPage: number = 10,
  search?: string,
  filters?: {
    activatedFilter?: string;
  }
): Promise<PaginatedBusinessesResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    perPage: perPage.toString(),
    ...filters,
  });
  if (search) {
    params.append('search', search);
  }
  const response = await apiInstance.get(
    `/admin/businesses?${params.toString()}`
  );
  return response.data;
};

export const exportBusinessEmails = async (
  search?: string,
  filters?: {
    activatedFilter?: string;
    userActivity?: string;
    subscriptionStatus?: string;
  }
): Promise<Blob> => {
  const params = new URLSearchParams({
    ...filters,
  });
  if (search) {
    params.append('search', search);
  }
  const response = await apiInstance.get(
    `/admin/businesses/export-emails?${params.toString()}`,
    {
      responseType: 'blob',
    }
  );
  return response.data;
};

export const getBusinessAdmin = async (
  id: string
): Promise<AdminBusinessInterface> => {
  const response = await apiInstance.get(`/admin/businesses/${id}`);
  return response.data;
};

export const updateBusinessAdmin = async (
  id: string,
  data: Partial<AdminBusinessInterface>
): Promise<{ message: string; business: AdminBusinessInterface }> => {
  const response = await apiInstance.put(`/admin/businesses/${id}`, data);
  return response.data;
};

export const changeBusinessOwner = async (
  businessId: string,
  newOwnerId: string
): Promise<{ message: string; member: BusinessMemberInterface }> => {
  const response = await apiInstance.put(
    `/admin/businesses/${businessId}/members/${newOwnerId}`,
    { isOwner: true }
  );
  return response.data;
};

export const addBusinessMember = async (
  businessId: string,
  userId: string,
  permissions?: BusinessMemberPermissions | null,
  jobTitle?: string
): Promise<{ message: string; member: BusinessMemberInterface }> => {
  const response = await apiInstance.post(
    `/admin/businesses/${businessId}/members`,
    {
      userId,
      permissions,
      jobTitle,
    }
  );
  return response.data;
};

export const removeBusinessMember = async (
  businessId: string,
  userId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await apiInstance.delete(
    `/admin/businesses/${businessId}/members/${userId}`
  );
  return response.data;
};

export const updateBusinessMember = async (
  businessId: string,
  userId: string,
  data: {
    permissions?: BusinessMemberPermissions | null;
    jobTitle?: string | null;
  }
): Promise<{ message: string; member: BusinessMemberInterface }> => {
  const response = await apiInstance.put(
    `/admin/businesses/${businessId}/members/${userId}`,
    data
  );
  return response.data;
};

export const changeBusinessPlan = async (
  businessId: string,
  planId: number
): Promise<{ message: string; subscription: unknown }> => {
  const response = await apiInstance.put(
    `/admin/businesses/${businessId}/plan`,
    {
      planId,
    }
  );
  return response.data;
};

export const searchUsersForBusiness = async (
  search: string,
  limit: number = 10
): Promise<BusinessMemberUserInterface[]> => {
  const params = new URLSearchParams({
    search,
    limit: limit.toString(),
  });
  const response = await apiInstance.get(
    `/admin/businesses/search-users?${params.toString()}`
  );
  return response.data;
};

export const getAllPlansAdmin = async (): Promise<PlanInterface[]> => {
  const response = await apiInstance.get('/admin/businesses/plans');
  return response.data;
};

export interface MembersListResponse extends ListResponse<BusinessMemberInterface> {
  seatLimit: number;
  seatsUsed: number;
  seatsLeft: number;
}

export const listBusinessMembersAdmin = async (
  businessId: string,
  page: number = 1,
  perPage: number = 10,
  search?: string
): Promise<MembersListResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    perPage: perPage.toString(),
  });
  if (search) {
    params.append('search', search);
  }
  const response = await apiInstance.get(
    `/admin/businesses/${businessId}/members?${params.toString()}`
  );
  return response.data;
};
